import { Box, Group, Stack } from '@mantine/core';
import Toast from 'components/Toast';
import { IAutomation } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import AutomationConditionsPreview from 'pages/revisions-module/template-editor/editors/pipeline/AutomationConditionsPreview';
import SetRevisionSafetyPointAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/SetRevisionSafetyPointAutomationPreview';
import PlanNextRevisionAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/PlanNextRevisionAutomationPreview';
import GatherConfirmationAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/GatherConfirmationAutomationPreview';
import SetDeviceEvidenceFieldAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/SetDeviceEvidenceFieldAutomationPreview';
import SendEmailAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/SendEmailAutomationPreview';
import SetFileNameAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/SetFileNameAutomationPreview';
import CreateChildDepartmentAutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/previews/CreateChildDepartmentAutomationPreview';
import { ReactNode } from 'react';
import { IconArrowsSplit, IconSettingsCode } from '@tabler/icons-react';

type TypeCond<T, U> = [T] extends [never] ? { message: U } : {};

/**
 * Displays a preview of the automation.
 */
export default function AutomationPreview({ data }: { data: IAutomation }) {
  const hasConditions = Boolean(data.runIfTrue) || (data.customerOrganizationIds?.length ?? 0) > 0;
  const conditionsPreview = hasConditions ? <AutomationConditionsPreview data={data} /> : null;

  /**
   * Renders the automation properties with optional conditions below.
   */
  const wrapPreview = (propertiesPreview: ReactNode) => (
    <Stack spacing={4}>
      <Group spacing={8} noWrap align="flex-start">
        <IconSettingsCode size={16} style={{ flexShrink: 0 }} />
        <Group spacing={8} sx={{ flex: 1, minWidth: 0 }}>
          {propertiesPreview}
        </Group>
      </Group>
      {conditionsPreview && (
        <Group spacing={8}>
          <IconArrowsSplit size={16} />
          {conditionsPreview}
        </Group>
      )}
    </Stack>
  );

  switch (data.type) {
    case 'set-revision-safety-point':
      return wrapPreview(<SetRevisionSafetyPointAutomationPreview data={data} />);

    case 'plan-next-revision':
      return wrapPreview(<PlanNextRevisionAutomationPreview data={data} />);

    case 'gather-confirmation':
      return wrapPreview(<GatherConfirmationAutomationPreview data={data} />);

    case 'set-device-evidence-field':
      return wrapPreview(<SetDeviceEvidenceFieldAutomationPreview data={data} />);

    case 'send-email':
      return wrapPreview(<SendEmailAutomationPreview data={data} />);

    case 'set-file-name':
      return wrapPreview(<SetFileNameAutomationPreview data={data} />);

    case 'create-child-department':
      return wrapPreview(<CreateChildDepartmentAutomationPreview data={data} />);

    default:
      // This is a TS trick to make sure all types are handled in the switch.
      // If there is a new type, the compiler will complain here. To fix it, add a new case to the switch.
      const { message }: TypeCond<typeof data, string> = { message: 'Neznámy proces' };

      return (
        <Box mr={32}>
          <Toast fullWidth type="fail" withCloseButton={false} message={message} />
        </Box>
      );
  }
}
